import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { format } from 'date-fns'
import { TrashIcon } from '@heroicons/react/24/outline'
import { Button } from 'src/components/button'
import { ListItemCard, ListItemRow } from 'src/components/layout/item-collection'
import {
  Statement,
  statementStatusEnum,
  statementStatusLabelKeys,
  statementStatusBadgeClasses,
} from 'src/services/statements/models'
import type { ViewMode } from 'src/hooks/use-view-mode'
import { cn } from 'src/utils/style'

interface StatementListItemProps {
  statement: Statement
  viewMode: ViewMode
  onDelete: (id: string, name: string) => void
}

export const StatementListItem = ({ statement, viewMode, onDelete }: StatementListItemProps) => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const isJobActive =
    statement.status === statementStatusEnum.PROCESSING ||
    statement.status === statementStatusEnum.RECONCILING
  const accountName = statement.paymentAccountRef?.name ?? t('statementsPage.unknownAccount')
  const institution = [
    statement.paymentAccountRef?.institution,
    statement.paymentAccountRef?.accountLastFour
      ? `•••• ${statement.paymentAccountRef.accountLastFour}`
      : null,
  ]
    .filter(Boolean)
    .join(' ')
  const createdLabel = statement.created
    ? format(new Date(statement.created), 'MMM d, yyyy')
    : t('statementsPage.unknownDate')
  const rowCountLabel = t('statementsPage.rowCount', { count: statement.rowCount })
  const openReview = () => navigate(`/expenses/statements/${statement.id}/review`)
  const cardSuffix = viewMode === 'card' ? '-card' : ''

  const statusBadge = (
    <span
      className={cn(
        'shrink-0 whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-medium',
        statementStatusBadgeClasses[statement.status] || 'bg-gray-100 text-gray-700',
      )}
      data-testid={`statement-status-${statement.id}${cardSuffix}`}
    >
      {statementStatusLabelKeys[statement.status]
        ? t(statementStatusLabelKeys[statement.status])
        : statement.status}
    </span>
  )

  const deleteButton = (
    <Button
      variant="ghost"
      tone="error"
      iconOnly
      size="sm"
      shape="square"
      type="button"
      onClick={(e) => {
        e.stopPropagation()
        onDelete(statement.id, accountName)
      }}
      disabled={isJobActive}
      className={cn('shrink-0 transition-colors', isJobActive && 'cursor-not-allowed opacity-50')}
      data-testid={`delete-statement-${statement.id}${cardSuffix}`}
      aria-label={t('statementsPage.deleteTitle')}
    >
      <TrashIcon className="h-4 w-4" aria-hidden="true" />
    </Button>
  )

  if (viewMode === 'list') {
    return (
      <ListItemRow onClick={openReview} data-testid={`statement-row-${statement.id}`}>
        <div className="min-w-0 flex-1">
          <p className="truncate font-medium text-text-primary" title={accountName}>
            {accountName}
          </p>
          <p className="mt-0.5 truncate text-sm text-text-placeholder">{createdLabel}</p>
        </div>
        <div className="flex shrink-0 items-center gap-3">
          <span className="whitespace-nowrap text-sm text-text-placeholder">{rowCountLabel}</span>
          {statusBadge}
          {deleteButton}
        </div>
      </ListItemRow>
    )
  }

  return (
    <ListItemCard onClick={openReview} data-testid={`statement-card-${statement.id}`}>
      <div className="flex min-w-0 items-start justify-between gap-2">
        <div className="min-w-0">
          <h3 className="truncate text-lg font-bold text-text-primary" title={accountName}>
            {accountName}
          </h3>
          {institution && (
            <p className="truncate text-sm text-text-placeholder" title={institution}>
              {institution}
            </p>
          )}
        </div>
        {deleteButton}
      </div>

      <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
        <div className="min-w-0">
          <dt className="text-xs text-text-secondary">{t('statementsPage.uploadedOn')}</dt>
          <dd className="truncate font-medium text-text-primary">{createdLabel}</dd>
        </div>
        <div className="min-w-0">
          <dt className="text-xs text-text-secondary">{t('statementsPage.rows')}</dt>
          <dd className="truncate font-medium text-text-primary">{statement.rowCount}</dd>
        </div>
      </dl>

      <div className="mt-auto flex min-w-0 items-center justify-between gap-3 border-t border-border pt-3">
        <span className="shrink-0 text-sm text-text-secondary">{t('statementsPage.status')}</span>
        {statusBadge}
      </div>
    </ListItemCard>
  )
}
